import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { Link } from "@/components/Interface/Link/Link";
import { useMagicLinkLogin } from "@/hooks/useMagicLinkLogin";
import { FCC } from "@/types";

import { WelcomeCard } from "./WelcomeCard";

type MagicLinkEmailFields = {
  emailAddress: string;
};

interface Props {
  onBack: () => void;
  onSuccess: () => void;
}

export const MagicLinkRequestForm: FCC<Props> = ({ onBack, onSuccess }) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<MagicLinkEmailFields>({
    defaultValues: { emailAddress: "" },
  });
  const { requestMagicLink, showRequestError, isRequesting } =
    useMagicLinkLogin();
  const [forceEnableButton, setForceEnableButton] = useState(false);

  useEffect(() => {
    if (isRequesting || isSubmitting) {
      setForceEnableButton(false);
      const timer = setTimeout(() => setForceEnableButton(true), 10000);

      return () => clearTimeout(timer);
    }

    setForceEnableButton(false);
  }, [isRequesting, isSubmitting]);

  const onSubmit = async (data: MagicLinkEmailFields) => {
    const result = await requestMagicLink(data.emailAddress);

    if (result.ok) {
      onSuccess();
    } else {
      showRequestError(result.reason);
    }
  };

  return (
    <WelcomeCard>
      <form
        data-testid="magic-link-request-form"
        className="flex w-full flex-col gap-6"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-2">
          <h1 className="text-headline-md font-semibold leading-sm text-foreground-primary">
            {t("translation:newWelcome.magicLink.request.title")}
          </h1>
          <p className="text-body-sm text-foreground-primary">
            {t("translation:newWelcome.magicLink.request.description")}
          </p>
        </div>

        <FormField<MagicLinkEmailFields>
          id="magicLinkEmailAddress"
          name="emailAddress"
          label={t("translation:Common.generalForm.fields.email.label")}
          type="email"
          autocomplete="email"
          register={register}
          errors={errors}
          options={{
            required: t(
              "translation:Common.generalForm.validation.emailRequired",
            ),
            pattern: {
              value: /\S+@\S+\.\S+/,
              message: t(
                "translation:Common.generalForm.validation.emailNotValid",
              ),
            },
          }}
        />

        <div className="flex flex-col items-center gap-4">
          <Button
            dataTestid="magic-link-request-submit"
            variant="primary"
            type="submit"
            disabled={!forceEnableButton && (isRequesting || isSubmitting)}
          >
            {t("Common.label.continue")}
          </Button>

          <Link type="button" onClick={onBack}>
            {t("translation:newWelcome.magicLink.request.backToPasswordLogin")}
          </Link>
        </div>
      </form>
    </WelcomeCard>
  );
};
